import { defineContract, listOf, nullableString, timestamp, z } from "./_base";
import { request } from "@/lib/http";

/*
 * Agents & pipelines — three execution systems, one screen.
 *
 *   spine (binari-agentics)   governed: policy, approvals, audit. Tenant "kontact".
 *   kontact.id agents         ReAct agents (agent_definitions / agent_runs).
 *   kontact.id pipelines      the product's own Temporal DSL (task queue x92-pipelines).
 *
 * Spine is reached through /spine/* — a dev-only Vite proxy that injects the
 * tenant key server-side (see vite.config.ts). `call()` prefixes /api, so the
 * spine contracts use a small dedicated caller below.
 */

const SPINE = "/spine";

async function spine<T extends z.ZodTypeAny>(schema: T, path: string, init?: { method?: "GET" | "POST" | "PATCH"; body?: unknown; signal?: AbortSignal }): Promise<z.infer<T>> {
  const res = await fetch(`${SPINE}${path}`, {
    method: init?.method ?? "GET",
    signal: init?.signal,
    headers: { accept: "application/json", ...(init?.body !== undefined ? { "content-type": "application/json" } : {}) },
    ...(init?.body !== undefined ? { body: JSON.stringify(init.body) } : {}),
  });
  const raw = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error((raw as { error?: string }).error ?? `${res.status} ${res.statusText}`);
  const parsed = schema.safeParse(raw);
  if (!parsed.success) throw new Error(`spine ${path} returned an unexpected shape`);
  return parsed.data;
}

/* ── spine ───────────────────────────────────────────────────────────────── */
export const spinePipelineSchema = z.object({
  id: z.string(), name: z.string(), version: z.string(), status: z.string(),
  description: nullableString, step_count: z.number().optional(),
  referenced_tools: z.array(z.string()).nullish().transform((v) => v ?? []),
  run_count: z.number().optional().default(0),
  last_run_status: nullableString, last_run_at: timestamp, created_at: timestamp,
});
export type SpinePipeline = z.infer<typeof spinePipelineSchema>;

export const spineRunSchema = z.object({
  id: z.string(), pipeline_name: z.string(), pipeline_id: z.string(), status: z.string(),
  created_at: timestamp, finished_at: timestamp, error: nullableString,
  started_by_name: nullableString, started_by_type: nullableString,
});
export type SpineRun = z.infer<typeof spineRunSchema>;

export const spineApprovalSchema = z.object({
  id: z.string(), run_id: z.string(), step_id: z.string(), tool: nullableString, status: z.string(),
  pipeline_name: nullableString, requested_at: timestamp, expires_at: timestamp,
  required_roles: z.array(z.string()).nullish().transform((v) => v ?? []),
  request: z.record(z.unknown()).nullish().transform((v) => v ?? {}),
  reason: nullableString, decided_at: timestamp,
});
export type SpineApproval = z.infer<typeof spineApprovalSchema>;

export const spineAuditSchema = z.object({
  event_type: z.string(), step_id: nullableString, tool: nullableString, created_at: timestamp,
  detail: z.record(z.unknown()).nullish().transform((v) => v ?? {}),
});

export const spineTriggerSchema = z.object({
  id: z.string(), name: z.string(), kind: z.string(), cron: nullableString, timezone: nullableString,
  pipeline_id: nullableString, pipeline_name: nullableString, enabled: z.union([z.boolean(), z.string()]).optional().transform((v) => v === true || v === "true"),
  last_fired_at: timestamp, created_at: timestamp, input_template: z.record(z.unknown()).nullish(),
}).passthrough();
export type SpineTrigger = z.infer<typeof spineTriggerSchema>;

export const spineApi = {
  setTriggerEnabled: (id: string, enabled: boolean) => spine(z.unknown(), `/v1/triggers/${id}`, { method: "PATCH" as never, body: { enabled } }),
  proposeSchedule: (v: { pipelineId: string; name: string; cron: string; timezone?: string; inputTemplate?: Record<string, unknown> }) =>
    spine(z.object({ id: z.string() }).passthrough(), "/v1/triggers", { method: "POST", body: { kind: "schedule", ...v } }),
  triggers: (signal?: AbortSignal) => spine(listOf(spineTriggerSchema), "/v1/triggers", { signal }),
  pipelines: (signal?: AbortSignal) => spine(listOf(spinePipelineSchema), "/v1/pipelines", { signal }),
  runs: (signal?: AbortSignal) => spine(z.object({ runs: z.array(spineRunSchema) }).transform((v) => v.runs), "/v1/runs?limit=25", { signal }), // control plane returns { runs, nextCursor }
  run: (id: string, signal?: AbortSignal) => spine(
    spineRunSchema.extend({ approvals: z.array(spineApprovalSchema).optional().default([]), audit: z.array(spineAuditSchema).optional().default([]) }).passthrough(),
    `/v1/runs/${id}`, { signal }),
  approvals: (signal?: AbortSignal) => spine(listOf(spineApprovalSchema), "/v1/approvals", { signal }),
  decide: (id: string, decision: "approved" | "rejected", reason?: string) =>
    spine(z.unknown(), `/v1/approvals/${id}/decision`, { method: "POST", body: { decision, ...(reason ? { reason } : {}) } }),
  tools: (signal?: AbortSignal) => spine(listOf(z.object({ name: z.string(), side_effect: z.string(), enabled: z.union([z.boolean(), z.string()]).transform((v) => v === true || v === "true"), scopes: z.array(z.string()).nullish() })), "/v1/tools", { signal }),
};

/* ── kontact.id agents (ReAct loop) ─────────────────────────────────────── */
export const agentDefSchema = z.object({
  id: z.string(), slug: z.string(), name: z.string(), description: nullableString,
  goal_template: nullableString, allowed_tools: z.array(z.string()).nullish().transform((v) => v ?? []),
  model_tier: nullableString, max_steps: z.number().optional(), enabled: z.boolean().optional().default(false),
  schedule: nullableString, last_scheduled_at: timestamp, is_system: z.boolean().optional(),
});
export type AgentDef = z.infer<typeof agentDefSchema>;

export const agentRunSchema = z.object({
  id: z.string(), agent_slug: z.string().nullable(), goal: nullableString, status: z.string(),
  final_answer: nullableString, steps_taken: z.number().optional().default(0), tokens_used: z.number().optional().default(0),
  model: nullableString, started_at: timestamp, finished_at: timestamp,
});
export type AgentRun = z.infer<typeof agentRunSchema>;

export const agentDefsContract = defineContract({
  name: "ai.agent-defs", method: "GET", path: () => "/ai/agent-defs",
  response: z.object({ agents: z.array(agentDefSchema).optional().default([]) }),
});
export const agentRunsContract = defineContract({
  name: "ai.agent.runs", method: "GET", path: () => "/ai/agent/runs",
  response: z.object({ runs: z.array(agentRunSchema).optional().default([]) }),
  params: (q: { limit?: number }) => ({ limit: String(q.limit ?? 20) }),
});
export const agentRunDetailContract = defineContract({
  name: "ai.agent.run", method: "GET", path: (i: { id: string }) => `/ai/agent/runs/${i.id}`,
  response: z.object({
    run: agentRunSchema,
    steps: z.array(z.object({ step_no: z.number().optional(), kind: nullableString, tool_name: nullableString, tool_args: z.unknown().optional(), content: nullableString, created_at: timestamp })).optional().default([]),
  }),
});

/* ── kontact.id pipelines (its own Temporal DSL) ────────────────────────── */
export const x92PipelineSchema = z.object({
  id: z.string().optional(), slug: z.string(), name: nullableString, status: nullableString,
  version: z.union([z.string(), z.number()]).nullish(), deployedAt: timestamp, updatedAt: timestamp,
}).passthrough();
export const x92RunSchema = z.object({
  id: z.string(), slug: nullableString, pipelineSlug: nullableString, status: z.string(),
  startedAt: timestamp, finishedAt: timestamp, error: nullableString, workflowId: nullableString,
}).passthrough();

export const x92PipelinesContract = defineContract({
  name: "pipelines.list", method: "GET", path: () => "/pipelines",
  response: z.object({ pipelines: z.array(x92PipelineSchema).optional().default([]) }),
});
export const x92RunsContract = defineContract({
  name: "pipelines.runs", method: "GET", path: () => "/pipelines/runs",
  response: z.object({ runs: z.array(x92RunSchema).optional().default([]) }),
  params: (q: { limit?: number }) => ({ limit: String(q.limit ?? 20) }),
});
export const queueStatsContract = defineContract({
  name: "queue.stats", method: "GET", path: () => "/queue/stats",
  response: z.object({ pending: z.number().optional().default(0), actioned: z.number().optional().default(0), skipped: z.number().optional().default(0), snoozed: z.number().optional().default(0), actionedToday: z.number().optional().default(0) }),
});

/* Quietly referenced so an unused-import lint never removes the http core. */
void request;

/* ── agent runs + routines (live loops) ─────────────────────────────────── */
export const runAgentContract = defineContract({
  name: "ai.agent.run", method: "POST", path: () => "/ai/agent/run",
  response: z.object({ runId: z.string().optional(), finalAnswer: nullableString, steps: z.array(z.unknown()).optional().default([]) }).passthrough(),
  params: (i: { agent: string; input?: string }) => ({ agent: i.agent, ...(i.input ? { input: i.input } : {}) }),
});
export const upsertAgentDefContract = defineContract({
  name: "ai.agent-defs.upsert", method: "POST", path: () => "/ai/agent-defs",
  response: z.unknown(),
  params: (i: { slug: string; name: string; goalTemplate: string; description?: string; schedule?: string | null; scheduleInput?: string | null; enabled?: boolean; allowedTools?: string[]; modelTier?: string; maxSteps?: number }) => i,
});
export const deleteAgentDefContract = defineContract({
  name: "ai.agent-defs.delete", method: "DELETE", path: (i: { slug: string }) => `/ai/agent-defs/${i.slug}`,
  response: z.unknown(),
});

/* The platform's own repeatable loops, as declared in the worker
 * (apps/worker/src/processors/ai-cron.ts). The API exposes no live status for
 * BullMQ repeatables yet, so these are shown as SCHEDULES with that caveat on
 * screen — never as fabricated "last ran" values. */
export const PLATFORM_LOOPS = [
  ["daily_profile", "0 2 * * *", "re-profile contacts with new data"],
  ["daily_score", "0 3 * * *", "auto-score influence / strength / risk"],
  ["daily_embed", "0 4 * * *", "refresh embeddings"],
  ["daily_brief", "0 8 * * *", "morning briefing"],
  ["scheduled_proactive", "0 6 * * *", "proactive insight cards → Feed"],
  ["scheduled_intelligence", "0 10 * * *", "intelligence pass"],
  ["proactive_risk_scan", "0 6 * * 1,4", "risk scan (Mon/Thu)"],
  ["proactive_council_review", "0 9 * * 2,5", "council review (Tue/Fri)"],
  ["proactive_network_health", "0 7 * * 0", "network health (Sun)"],
  ["detect_life_events", "*/30 * * * *", "life-event detection"],
  ["program_of_work_nightly", "*/30 * * * *", "program of work"],
  ["enrich_assess", "*/20 * * * *", "relationship assessment"],
  ["enrich_noiseness", "0 15 * * *", "chat noise scoring"],
  ["agent_routines", "17 * * * *", "fires due agent routines (below)"],
] as const;

/** PATCH /ai/agent-defs/:slug — the real enable/disable + reschedule seam
 *  (COALESCE semantics: only the keys you send change). */
export const patchAgentDefContract = defineContract({
  name: "ai.agent-defs.patch", method: "PATCH", path: (i: { slug: string; enabled?: boolean; schedule?: string | null; scheduleInput?: string | null }) => `/ai/agent-defs/${i.slug}`,
  response: z.unknown(),
  params: ({ slug: _s, ...body }) => body,
});
